import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, CalendarCheck, CheckSquare,
  CheckCircle2, Circle, Clock, AlertCircle, ExternalLink, Check, Download, X
} from 'lucide-react';
import Header from '../components/layout/Header';
import StoryDetailModal from '../components/kanban/StoryDetailModal';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { exportToExcel } from '../utils/exportExcel';
import { formatDate } from '../utils/helpers';
import './MyTasks.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay(); // 0 = Sunday
}

function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Determine task color category using start_date (fallback created_at) and due_date
function getTaskCategory(task, today) {
  if (task.status === 'done') return 'completed';
  const due = parseLocalDate(task.due_date);
  const start = parseLocalDate(task.start_date || task.created_at);
  if (!due) return 'upcoming';
  if (due < today) return 'overdue';
  if (start && start <= today && today <= due) return 'active';
  if (start && start > today) return 'upcoming';
  return 'upcoming';
}

// Greedy lane assignment so bars don't overlap
function assignLanes(bars) {
  const laneEnds = [];
  const result = bars.map(bar => {
    let lane = laneEnds.findIndex(end => end < bar.startCol);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
    laneEnds[lane] = bar.endCol;
    return { ...bar, lane };
  });
  return { bars: result, laneCount: laneEnds.length };
}

// ── Week Row ──────────────────────────────────────────────────────────────────
function WeekRow({ weekDays, taskBars, today, onToggleComplete, onViewStory }) {
  const { bars, laneCount } = assignLanes(taskBars);
  const BAR_H = 24;
  const BAR_GAP = 3;
  const eventsHeight = laneCount > 0 ? laneCount * (BAR_H + BAR_GAP) + 8 : 32;

  return (
    <div className="cal-week-row">
      {/* Day number cells */}
      <div className="cal-week-days">
        {weekDays.map((day, i) => {
          const isToday = day && toLocalDateStr(day) === toLocalDateStr(today);
          return (
            <div key={i} className={`cal-day-cell${!day ? ' cal-day-empty' : ''}${isToday ? ' cal-day-today' : ''}`}>
              {day && (
                <span className={`cal-day-num${isToday ? ' cal-day-num-today' : ''}`}>
                  {day.getDate()}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Event bars */}
      <div className="cal-events-area" style={{ height: eventsHeight }}>
        {bars.map((bar, idx) => {
          const leftPct = (bar.startCol / 7) * 100;
          const widthPct = (bar.span / 7) * 100;
          const top = bar.lane * (BAR_H + BAR_GAP) + 4;
          const isDone = bar.category === 'completed';

          return (
            <div
              key={idx}
              className={`cal-task-bar cal-task-bar--${bar.category}`}
              style={{
                left: `calc(${leftPct}% + 3px)`,
                width: `calc(${widthPct}% - 6px)`,
                top: `${top}px`,
                height: `${BAR_H}px`,
              }}
            >
              <button
                className={`cal-task-toggle${isDone ? ' cal-task-toggle--done' : ''}`}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => { e.stopPropagation(); onToggleComplete(bar.task); }}
                title={isDone ? 'Mark incomplete' : 'Mark complete'}
              >
                {isDone ? <CheckCircle2 size={13} /> : <Circle size={13} />}
              </button>
              <button
                className="cal-task-bar-body"
                onMouseDown={e => e.stopPropagation()}
                onClick={e => { e.stopPropagation(); if (bar.task.task_type !== 'prospect' && bar.task.story_id) onViewStory(bar.task.story_id); }}
                title={bar.task.task_type === 'prospect' ? `Prospect: ${bar.task.prospect_title}` : `View story: ${bar.task.story_title}`}
              >
                <span className="cal-task-bar-title">{bar.task.title}</span>
                <span className="cal-task-bar-story"> · {bar.task.story_title || bar.task.prospect_title}</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyTasks() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingStoryId, setViewingStoryId] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);
  const [responseDetails, setResponseDetails] = useState('');
  const [storyFilter, setStoryFilter] = useState('');

  const [prospectFilter, setProspectFilter] = useState('');

  const storyOptions = useMemo(() => {
    const map = {};
    tasks.filter(t => t.task_type === 'story' && t.story_id).forEach(t => { map[t.story_id] = t.story_title; });
    return Object.entries(map).map(([id, title]) => ({ id, title }));
  }, [tasks]);

  const prospectOptions = useMemo(() => {
    const map = {};
    tasks.filter(t => t.task_type === 'prospect' && t.prospect_id).forEach(t => { map[t.prospect_id] = t.prospect_title; });
    return Object.entries(map).map(([id, title]) => ({ id, title }));
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (storyFilter && t.story_id !== storyFilter) return false;
      if (prospectFilter && t.prospect_id !== prospectFilter) return false;
      return true;
    });
  }, [tasks, storyFilter, prospectFilter]);

  const loadTasks = useCallback(async () => {
    try {
      const res = await api.get('/tasks/my');
      setTasks(res.data.tasks || []);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const getTaskEndpoint = (task) => {
    if (task.task_type === 'prospect') return `/prospect-tasks/${task.id}`;
    if (task.task_type === 'adhoc') return `/adhoc-tasks/${task.id}`;
    return `/tasks/${task.id}`;
  };

  const handleToggleComplete = async (task) => {
    if (task.status !== 'done') {
      setCompletingTask(task);
      setResponseDetails('');
      return;
    }
    try {
      await api.put(getTaskEndpoint(task), { status: 'todo' });
      toast.success('Task reopened!');
      loadTasks();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleConfirmComplete = async () => {
    if (!responseDetails.trim() || !completingTask) return;
    try {
      await api.put(getTaskEndpoint(completingTask), { status: 'done', response_details: responseDetails.trim() });
      toast.success('Task marked complete!');
      setCompletingTask(null);
      setResponseDetails('');
      loadTasks();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const slots = [];
  for (let i = 0; i < firstDay; i++) slots.push(null);
  for (let d = 1; d <= daysInMonth; d++) slots.push(new Date(year, month, d));
  while (slots.length % 7 !== 0) slots.push(null);
  const weeks = [];
  for (let i = 0; i < slots.length; i += 7) weeks.push(slots.slice(i, i + 7));

  // Compute task bars for a week
  const getWeekBars = (weekDays) => {
    const weekStart = weekDays.find(d => d !== null);
    const weekEnd = [...weekDays].reverse().find(d => d !== null);
    if (!weekStart || !weekEnd) return [];

    const bars = [];
    filteredTasks.forEach(task => {
      // Use start_date if available, else created_at
      const taskStart = parseLocalDate(task.start_date || task.created_at);
      const taskEnd = parseLocalDate(task.due_date);
      if (!taskStart || !taskEnd) return;
      if (taskEnd < weekStart || taskStart > weekEnd) return;

      const clampedStart = taskStart < weekStart ? weekStart : taskStart;
      const clampedEnd = taskEnd > weekEnd ? weekEnd : taskEnd;

      const startCol = weekDays.findIndex(d => d && toLocalDateStr(d) === toLocalDateStr(clampedStart));
      const endCol = weekDays.findIndex(d => d && toLocalDateStr(d) === toLocalDateStr(clampedEnd));
      if (startCol === -1 || endCol === -1) return;

      const category = getTaskCategory(task, today);
      const sortOrder = category === 'active' ? 0 : category === 'overdue' ? 1 : category === 'upcoming' ? 2 : 3;
      bars.push({ task, startCol, endCol, span: endCol - startCol + 1, category, sortOrder });
    });

    bars.sort((a, b) => a.sortOrder - b.sortOrder || a.startCol - b.startCol);
    return bars;
  };

  // Stats
  const totalTasks = filteredTasks.length;
  const completedCount = filteredTasks.filter(t => t.status === 'done').length;
  const overdueCount = filteredTasks.filter(t => getTaskCategory(t, today) === 'overdue').length;
  const activeCount = filteredTasks.filter(t => getTaskCategory(t, today) === 'active').length;

  if (loading) return (
    <div className="page-loading">
      <div className="page-spinner" />
      <p>Loading your tasks...</p>
    </div>
  );

  return (
    <>
      {completingTask && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setCompletingTask(null)}>
          <div style={{ background: 'white', borderRadius: 12, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid var(--border-light)' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Complete Task</h3>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }} onClick={() => setCompletingTask(null)}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}><strong>{completingTask.title}</strong></p>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                  Task Response Details <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="Describe what was done, outcomes, notes…"
                  value={responseDetails}
                  onChange={e => setResponseDetails(e.target.value)}
                  autoFocus
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                />
                {!responseDetails.trim() && (
                  <p style={{ fontSize: 11, color: '#dc3545', marginTop: 4, marginBottom: 0 }}>Response details are required before marking complete.</p>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--border-light)' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setCompletingTask(null)}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleConfirmComplete}
                disabled={!responseDetails.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Check size={14} /> Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}
      {viewingStoryId && (
        <StoryDetailModal
          storyId={viewingStoryId}
          columns={[]}
          users={[]}
          readOnly={true}
          onClose={() => { setViewingStoryId(null); loadTasks(); }}
          onUpdated={loadTasks}
        />
      )}
      <Header title="My Tasks" subtitle="Your assigned tasks across all user stories" />
      <div className="page-content mytasks-page">

        {/* Stats row */}
        <div className="mytasks-stats">
          <div className="mytasks-stat">
            <div className="mytasks-stat-icon mytasks-stat-icon--active"><Clock size={16} /></div>
            <div>
              <div className="mytasks-stat-value">{activeCount}</div>
              <div className="mytasks-stat-label">In Progress</div>
            </div>
          </div>
          <div className="mytasks-stat">
            <div className="mytasks-stat-icon mytasks-stat-icon--overdue"><AlertCircle size={16} /></div>
            <div>
              <div className="mytasks-stat-value">{overdueCount}</div>
              <div className="mytasks-stat-label">Overdue</div>
            </div>
          </div>
          <div className="mytasks-stat">
            <div className="mytasks-stat-icon mytasks-stat-icon--completed"><CheckSquare size={16} /></div>
            <div>
              <div className="mytasks-stat-value">{completedCount}</div>
              <div className="mytasks-stat-label">Completed</div>
            </div>
          </div>
          <div className="mytasks-stat">
            <div className="mytasks-stat-icon mytasks-stat-icon--total"><CalendarCheck size={16} /></div>
            <div>
              <div className="mytasks-stat-value">{totalTasks}</div>
              <div className="mytasks-stat-label">Total Tasks</div>
            </div>
          </div>
        </div>

        {/* Legend + Export */}
        <div className="mytasks-legend">
          <span className="legend-item"><span className="legend-dot legend-active" /> Active</span>
          <span className="legend-item"><span className="legend-dot legend-upcoming" /> Upcoming</span>
          <span className="legend-item"><span className="legend-dot legend-overdue" /> Overdue</span>
          <span className="legend-item"><span className="legend-dot legend-completed" /> Completed</span>
          <span className="legend-hint">Click the circle on a task bar to mark complete / incomplete</span>
          <select className="filter-select" value={storyFilter} onChange={e => setStoryFilter(e.target.value)}>
            <option value="">All Stories</option>
            {storyOptions.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <select className="filter-select" value={prospectFilter} onChange={e => setProspectFilter(e.target.value)}>
            <option value="">All Prospects</option>
            {prospectOptions.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const data = tasks.map(t => ({
                'Title': t.title,
                'Story Title': t.story_title || '',
                'Status': t.status || '',
                'Priority': t.priority || '',
                'Start Date': formatDate(t.start_date),
                'Due Date': formatDate(t.due_date),
              }));
              exportToExcel(data, 'my-tasks');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
          >
            <Download size={15} /> Export Excel
          </button>
        </div>

        {/* Calendar */}
        <div className="cal-card">
          {/* Navigation */}
          <div className="cal-nav">
            <button className="cal-nav-btn cal-nav-arrow" onClick={prevMonth} aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <div className="cal-nav-title">
              <span className="cal-nav-month">{MONTH_NAMES[month]}</span>
              <span className="cal-nav-year">{year}</span>
            </div>
            <button className="cal-nav-btn cal-nav-today" onClick={goToday}>Today</button>
            <button className="cal-nav-btn cal-nav-arrow" onClick={nextMonth} aria-label="Next month">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day headers */}
          <div className="cal-day-headers">
            {DAY_NAMES.map(d => (
              <div key={d} className="cal-day-header">{d}</div>
            ))}
          </div>

          {/* Weeks */}
          <div className="cal-grid">
            {weeks.map((weekDays, wi) => (
              <WeekRow
                key={wi}
                weekDays={weekDays}
                taskBars={getWeekBars(weekDays)}
                today={today}
                onToggleComplete={handleToggleComplete}
                onViewStory={setViewingStoryId}
              />
            ))}
          </div>
        </div>

        {/* Task list below calendar */}
        {filteredTasks.length > 0 && (
          <div className="mytasks-list-section">
            <h3 className="mytasks-list-title">All Assigned Tasks</h3>
            <div className="mytasks-list">
              {filteredTasks.map(task => {
                const cat = getTaskCategory(task, today);
                const isDone = task.status === 'done';
                const startDate = parseLocalDate(task.start_date || task.created_at);
                const dueDate = parseLocalDate(task.due_date);
                return (
                  <div key={task.id} className={`mytasks-list-item mytasks-list-item--${cat}`}>
                    <button
                      className={`task-check${isDone ? ' checked' : ''}`}
                      onClick={() => handleToggleComplete(task)}
                      title={isDone ? 'Mark incomplete' : 'Mark complete'}
                    >
                      {isDone && <Check size={11} />}
                    </button>
                    <div className="mytasks-list-info">
                      <span className={`mytasks-list-task-title${isDone ? ' mytasks-list-task-title--done' : ''}`}>
                        {task.title}
                      </span>
                      <span className="mytasks-list-story">
                        {task.story_title || task.prospect_title || ''}
                        {task.task_type === 'prospect' && (
                          <span style={{ fontSize: 9, background: '#f59e0b22', color: '#d97706', borderRadius: 4, padding: '1px 5px', marginLeft: 5, fontWeight: 600 }}>PROSPECT</span>
                        )}
                        {task.task_type === 'adhoc' && (
                          <span style={{ fontSize: 9, background: '#3e72ae22', color: '#3e72ae', borderRadius: 4, padding: '1px 5px', marginLeft: 5, fontWeight: 600 }}>AD-HOC</span>
                        )}
                      </span>
                    </div>
                    <div className="mytasks-list-right">
                      {(startDate || dueDate) && (
                        <span className={`mytasks-list-dates mytasks-list-dates--${cat}`}>
                          {startDate ? startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                          {' → '}
                          {dueDate ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No due date'}
                        </span>
                      )}
                      <span className={`mytasks-list-badge mytasks-list-badge--${cat}`}>
                        {cat === 'active' ? 'In Progress' : cat === 'overdue' ? 'Overdue' : cat === 'upcoming' ? 'Upcoming' : 'Completed'}
                      </span>
                      {task.task_type !== 'prospect' && task.task_type !== 'adhoc' && task.story_id && (
                        <button
                          className="mytasks-view-story-btn"
                          onClick={() => setViewingStoryId(task.story_id)}
                          title="View story"
                        >
                          <ExternalLink size={13} />
                          <span>Story</span>
                        </button>
                      )}
                      {task.task_type === 'adhoc' && task.story_id && (
                        <button
                          className="mytasks-view-story-btn"
                          onClick={() => setViewingStoryId(task.story_id)}
                          title="View linked story"
                        >
                          <ExternalLink size={13} />
                          <span>Story</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
